import { FocusEvent, useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { format } from 'date-fns/format';
import { de as LocaleDe } from 'date-fns/locale/de';

import type { LanguageCode } from '@silvertours/common-landingpages-view';
import { analytics, useKeyNavigation } from '@silvertours/front-entities';
import { String } from '@silvertours/front-shared';
import { I18nLegacy } from '@silvertours/front-legacy-shared';
import { useSearchContext } from '../context/useSearchContext';
import {
  getDefaultDepartureDatePerLanguage,
  getDefaultDestinationDate,
} from '../context/reducer';
import { SearchFormState } from '../context/types';
import {
  formatDate as formatDateForForm,
  isDeptDateInLessThanDays,
  isDestDateBeforeDepDate,
} from '../context/utils';

import type { SearchHistoryItem } from './types';
import {
  Wrapper,
  Headline,
  List,
  ItemButton,
  Destination,
  Duration,
  DeleteButton,
} from './SearchHistory.styles';
import { clearSearchHistory } from './utils';

const mapHistoryItemToForm = (
  item: SearchHistoryItem,
  language: LanguageCode,
): Partial<SearchFormState> => {
  const { depLocation, destLocation, depDate, depTime, destDate, destTime } =
    item;

  const depDateTime = `${depDate}T${depTime}`;
  const destDateTime = `${destDate}T${destTime}`;

  const revertToDefaultDates =
    isDeptDateInLessThanDays(depDateTime, 0) ||
    isDestDateBeforeDepDate(destDateTime, depDateTime);

  return {
    depLocation,
    destLocation,
    depDate: revertToDefaultDates
      ? formatDateForForm(getDefaultDepartureDatePerLanguage(language))
      : depDateTime,
    destDate: revertToDefaultDates
      ? formatDateForForm(getDefaultDestinationDate())
      : destDateTime,
  };
};

interface SearchHistoryProps {
  onLastElementBlur: (event?: FocusEvent<HTMLButtonElement, Element>) => void;
  onItemClick: () => void;
  onItemSelect: (id?: string) => void;
}

export const SearchHistory = ({
  onItemClick,
  onLastElementBlur,
  onItemSelect,
}: SearchHistoryProps) => {
  const t = useTranslations('features.stageLegacy.stage.searchForm');
  const { language } = I18nLegacy.useLocale();
  const {
    getHistoryItemsByLanguage,
    setSearchFromHistory,
    clearSearchHistory: clearHistoryState,
  } = useSearchContext();
  const listRef = useRef<HTMLUListElement>(null);

  const handleSetItem = useCallback((item: SearchHistoryItem) => {
    setSearchFromHistory(mapHistoryItemToForm(item, language));
    analytics.gtm.trackSearchHistorySelect();
    analytics.generic.trackSearchHistorySelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearItem = useCallback(() => {
    clearSearchHistory();
    clearHistoryState();
    analytics.gtm.trackSearchHistoryDelete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const localDateFormats =
    String.dateFormats[language as keyof typeof String.dateFormats];

  const formatDateLong = (value: string) =>
    format(new Date(value), localDateFormats.dateLong, {
      locale: LocaleDe,
    });

  const formatDuration = (start: string, end: string) =>
    [formatDateLong(start), formatDateLong(end)].join(' – ');

  const items = getHistoryItemsByLanguage(language);

  const handleEnter = (item?: SearchHistoryItem) => {
    if (!item) return;
    handleSetItem(item);
    onItemClick();
  };

  const handleEscape = () => {
    onLastElementBlur();
  };

  const { focusedItem, onMouseEnter, onMouseLeave } =
    useKeyNavigation<SearchHistoryItem>(
      items || [],
      listRef,
      handleEnter,
      handleEscape,
    );

  useEffect(() => {
    onItemSelect(
      focusedItem === null ? undefined : `search-history-item-${focusedItem}`,
    );
  }, [focusedItem, onItemSelect]);

  if (items.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <Headline id="search-history-title">{t('searchHistory.title')}</Headline>
      <List
        ref={listRef}
        id="search-history-list"
        role="grid"
        aria-labelledby="search-history-title"
      >
        {items.map((item, index) => (
          <li
            // We pass the index as part of the key since we are not modifying this list on the same render.
            // eslint-disable-next-line react/no-array-index-key
            key={item.depLocation.name + index}
            role="presentation"
          >
            <ItemButton
              type="button"
              id={`search-history-item-${index}`}
              tabIndex={0}
              onMouseDown={e => {
                e.stopPropagation();
                handleSetItem(item);
                onItemClick();
              }}
              onMouseEnter={() => onMouseEnter(index)}
              onMouseLeave={onMouseLeave}
              role="row"
              aria-selected={index === focusedItem}
            >
              <Destination role="gridcell">
                {item.depLocation.name}{' '}
                {item.destLocation ? ` - ${item.destLocation.name}` : ''}
              </Destination>
              <Duration role="gridcell">
                {formatDuration(item.depDate, item.destDate)}
              </Duration>
            </ItemButton>
          </li>
        ))}
        <li role="presentation">
          {/* using onMouseDown on the button to guarantee that it is still rendered before losing focus, triggering the event */}
          <DeleteButton
            type="button"
            id={`search-history-item-${items.length}`}
            tabIndex={0}
            onMouseDown={handleClearItem}
            onMouseEnter={() => onMouseEnter(items.length)}
            onMouseLeave={onMouseLeave}
            onBlur={onLastElementBlur}
            role="row"
            aria-selected={items.length === focusedItem}
          >
            <span role="gridcell">{t('searchHistory.deleteLink')}</span>
          </DeleteButton>
        </li>
      </List>
    </Wrapper>
  );
};
